import './branch-form-infos.scss';
import React, { useEffect, useState } from 'react';
import { ClbButton } from '@celebration/react/ClbButton/ClbButton';
import { ZodFormattedError } from 'zod';
import { useTranslation } from 'react-i18next';
import Shape from '../../../../../../common/components/shape';
import ValidationService from '../../../../../../common/services/validation';
import IBranchFormInfosProps from './interfaces/IBranchFormInfosProps';
import IContact, {
    ContactType,
} from '../../../../../../common/interfaces/IContacts';
import { IContactErrors } from '../../../../interfaces/IBranchFormDataErrors';
import { EventType } from '../../interfaces/IHandleFormChangeProps';
import Input from '../../../../../../common/components/input';
import placeholderPatterns from '../../../../../../common/components/input/placeholder-patterns';
import {
    MASKTYPE,
    TYPE,
} from '../../../../../../common/components/input/input-constants';
import { cnpjMask, phoneMask } from '../../../../../../utils/masks-constants';
import SelectAutocomplete from '../../../../../../common/components/select-autocomplete';

function BranchFormInfos({
    handleFormChange,
    branchFormData,
    initialStateCarrier,
    addNewAdditionalContact,
    inputErrors,
}: Readonly<IBranchFormInfosProps>) {
    const { t } = useTranslation();

    const corporateDataTitle = t('FilialDadosEmpresa');
    const systemDataTitle = t('DadoSistema');
    const contactInfosTitle = t('FilialInformacaoContato');
    const carrierContactSubTitle = t('FilialContatoMatriz');
    const principalContactSubTitle = t('FilialContatoPrincipal');

    const [errorsOnExistingData, setErrorsOnExistingData] = useState<
        boolean | undefined
    >(undefined);

    useEffect(() => {
        if (branchFormData?.id && errorsOnExistingData === undefined) {
            setErrorsOnExistingData(!inputErrors?._errors);
        }
    }, [branchFormData?.id, inputErrors, errorsOnExistingData]);

    const renderCorporateData = () => {
        return (
            <>
                <h1> {corporateDataTitle} </h1>
                <div className="corporate-data-div">
                    <Input
                        id="cnpj"
                        label="CNPJ"
                        mask={MASKTYPE.CNPJ}
                        placeholder={placeholderPatterns.CNPJ}
                        value={branchFormData?.cnpj}
                        onChange={(e: any) =>
                            handleFormChange({
                                property: 'cnpj',
                                newValue: cnpjMask(e.target.value),
                            })
                        }
                        error={!!inputErrors?.cnpj}
                        helperText={ValidationService.getError(
                            inputErrors?.cnpj,
                            t,
                        )}
                        dataTestId="cnpj"
                    />
                    <Input
                        id="corporate-name"
                        label={t('RazaoSocial')}
                        value={branchFormData?.corporateName}
                        className="corporate-name"
                        placeholder={t('InsiraNomeEmpresa')}
                        onChange={(e: any) =>
                            handleFormChange({
                                property: 'corporateName',
                                newValue: e.target.value,
                            })
                        }
                        error={!!inputErrors?.corporateName}
                        helperText={ValidationService.getError(
                            inputErrors?.corporateName,
                            t,
                        )}
                        dataTestId="corporate-name"
                    />
                    <Input
                        id="paymentTerm"
                        mask="decimal_small"
                        label={t('TransportadoraPrazoPagamento')}
                        placeholder={placeholderPatterns.DECIMAL_SMALL}
                        value={branchFormData?.paymentTerm}
                        onChange={(e: any) =>
                            handleFormChange({
                                property: 'paymentTerm',
                                newValue: e.target.value,
                            })
                        }
                        error={!!inputErrors?.paymentTerm}
                        helperText={ValidationService.getError(
                            inputErrors?.paymentTerm,
                            t,
                        )}
                        dataTestId="payment-term"
                    />
                </div>
            </>
        );
    };

    const renderSystemData = () => {
        return (
            <>
                <h1> {systemDataTitle} </h1>
                <div className="system-data-div">
                    <Input
                        id="tms-code"
                        label={t('CodigoTms')}
                        value={branchFormData?.tmsCode}
                        placeholder={t('CodigoTmsInsira')}
                        onChange={(e: any) =>
                            handleFormChange({
                                property: 'tmsCode',
                                newValue: e.target.value,
                            })
                        }
                        counterEnabled
                        maxlength={10}
                        error={!!inputErrors?.tmsCode}
                        helperText={ValidationService.getError(
                            inputErrors?.tmsCode,
                            t,
                        )}
                        dataTestId="tms-code"
                    />
                    <Input
                        id="sap-code"
                        label={t('CodigoSap')}
                        placeholder={placeholderPatterns.SAP_S4}
                        maxlength={10}
                        value={branchFormData?.sapCode}
                        onChange={(e: any) =>
                            handleFormChange({
                                property: 'sapCode',
                                newValue: e.target.value,
                            })
                        }
                        counterEnabled
                        error={!!inputErrors?.sapCode}
                        helperText={ValidationService.getError(
                            inputErrors?.sapCode,
                            t,
                        )}
                        dataTestId="sap-code"
                    />
                    <Input
                        id="ecc-code"
                        label={t('CodigoEcc')}
                        type={TYPE.NUMBER}
                        placeholder={placeholderPatterns.SAP_ECC}
                        maxlength={6}
                        value={branchFormData?.eccCode}
                        onChange={(e: any) =>
                            handleFormChange({
                                property: 'eccCode',
                                newValue: e.target.value,
                            })
                        }
                        counterEnabled
                        error={!!inputErrors?.eccCode}
                        helperText={ValidationService.getError(
                            inputErrors?.eccCode,
                            t,
                        )}
                        dataTestId="ecc-code"
                    />
                </div>
            </>
        );
    };

    const renderContact = (
        contactPrefix: string,
        contactEventType: EventType,
        contact?: IContact,
        contactErrors?: ZodFormattedError<IContactErrors, string>,
        index?: number,
    ) => {
        const indexSuffix = index !== undefined ? `-${index}` : '';

        return (
            <>
                <div className="principal-email">
                    <Input
                        id={`${contactPrefix}-email${indexSuffix}`}
                        label="E-mail"
                        placeholder={placeholderPatterns.EMAIL}
                        value={contact?.email}
                        onChange={(e: any) =>
                            handleFormChange({
                                property: 'email',
                                newValue: e.target.value,
                                eventType: contactEventType,
                                index,
                            })
                        }
                        disabled={
                            contactEventType ===
                                EventType.CarriersContactChange &&
                            !!initialStateCarrier?.carrierContact?.email
                        }
                        error={!!contactErrors?.email}
                        helperText={ValidationService.getError(
                            contactErrors?.email,
                            t,
                        )}
                        dataTestId={`${contactPrefix}-mail${indexSuffix}`}
                    />
                </div>

                <div className="div-flex">
                    <div className="margin-phone">
                        <Input
                            id={`${contactPrefix}-phone${indexSuffix}`}
                            label={t('Telefone')}
                            placeholder={placeholderPatterns.PHONE}
                            mask={MASKTYPE.PHONE}
                            value={contact?.phone}
                            disabled={
                                contactEventType ===
                                    EventType.CarriersContactChange &&
                                !!initialStateCarrier?.carrierContact?.phone
                            }
                            onChange={(e: any) =>
                                handleFormChange({
                                    property: 'phone',
                                    newValue: phoneMask(e.target.value),
                                    eventType: contactEventType,
                                    index,
                                })
                            }
                            error={!!contactErrors?.phone}
                            helperText={ValidationService.getError(
                                contactErrors?.phone,
                                t,
                            )}
                            dataTestId={`${contactPrefix}-phone${indexSuffix}`}
                        />
                    </div>

                    <div className="div-select">
                        <SelectAutocomplete
                            key={0}
                            label={t('TipoTelefone')}
                            placeholder="Select"
                            disabled={
                                contactEventType ===
                                    EventType.CarriersContactChange &&
                                !!initialStateCarrier?.carrierContact
                                    ?.contactType
                            }
                            options={[
                                {
                                    label: t('Operacional'),
                                    value: ContactType.operational,
                                },
                                {
                                    label: t('Comercial'),
                                    value: ContactType.commercial,
                                },
                            ]}
                            onChange={(e: any) =>
                                handleFormChange({
                                    property: 'contactType',
                                    newValue: e,
                                    eventType: contactEventType,
                                    index,
                                })
                            }
                            defaultValue={
                                contact?.contactType ?? ContactType.operational
                            }
                            dataTestId={`${contactPrefix}-phone-type${indexSuffix}`}
                        />
                    </div>

                    <div>
                        <Input
                            id={`${contactPrefix}-responsible${indexSuffix}`}
                            label={t('Responsavel')}
                            placeholder={t('ResponsavelInserir')}
                            value={contact?.responsible}
                            disabled={
                                contactEventType ===
                                    EventType.CarriersContactChange &&
                                !!initialStateCarrier?.carrierContact
                                    ?.responsible
                            }
                            onChange={(e: any) =>
                                handleFormChange({
                                    property: 'responsible',
                                    newValue: e.target.value,
                                    eventType: contactEventType,
                                    index,
                                })
                            }
                            error={!!contactErrors?.responsible}
                            helperText={ValidationService.getError(
                                contactErrors?.responsible,
                                t,
                            )}
                            dataTestId={`${contactPrefix}-responsible${indexSuffix}`}
                        />
                    </div>
                    <div>
                        <Input
                            id={`${contactPrefix}-function-input${indexSuffix}`}
                            label={t('Funcao')}
                            placeholder={t('FuncaoInsereResponsavel')}
                            value={contact?.occupation}
                            disabled={
                                contactEventType ===
                                    EventType.CarriersContactChange &&
                                !!initialStateCarrier?.carrierContact
                                    ?.occupation
                            }
                            onChange={(e: any) =>
                                handleFormChange({
                                    property: 'occupation',
                                    newValue: e.target.value,
                                    eventType: contactEventType,
                                    index,
                                })
                            }
                            error={!!contactErrors?.occupation}
                            helperText={ValidationService.getError(
                                contactErrors?.occupation,
                                t,
                            )}
                            dataTestId={`${contactPrefix}-responsible-function${indexSuffix}`}
                        />
                    </div>
                </div>
            </>
        );
    };

    const renderCarrierContact = () => {
        return (
            <>
                <h1> {contactInfosTitle} </h1>

                <h2> {carrierContactSubTitle} </h2>

                {renderContact(
                    'carrier-contact',
                    EventType.CarriersContactChange,
                    branchFormData?.carriersContact,
                    inputErrors?.carriersContact,
                )}
            </>
        );
    };

    const renderMainContact = () => {
        return (
            <>
                <h2> {principalContactSubTitle} </h2>

                {renderContact(
                    'main-contact',
                    EventType.MainContactChange,
                    branchFormData?.mainContact,
                    inputErrors?.mainContact,
                )}
            </>
        );
    };

    const renderAdditionalContacts = () => {
        return branchFormData.additionalContacts.map(
            (currentElement: IContact, index: number) => {
                const additionalContactErrors =
                    inputErrors?.additionalContacts === undefined
                        ? ({} as ZodFormattedError<IContactErrors>)
                        : inputErrors.additionalContacts[index];

                return (
                    <div
                        className="margin-additional-contact"
                        // eslint-disable-next-line react/no-array-index-key
                        key={`additional-contact-${index}`}
                    >
                        {renderContact(
                            'additional-contact',
                            EventType.AdditionalContactChange,
                            currentElement,
                            additionalContactErrors,
                            index,
                        )}
                    </div>
                );
            },
        );
    };

    return (
        <Shape>
            {renderCorporateData()}
            {renderSystemData()}
            {renderCarrierContact()}
            {renderMainContact()}
            {renderAdditionalContacts()}

            <ClbButton
                styleType="tertiary"
                onClick={() => addNewAdditionalContact()}
                data-testid="add-additional-contact-button"
                icon="add"
                iconPosition="left"
                aria-label="add-additional-contact"
                label={t('FilialAdicionarContato')}
            />
        </Shape>
    );
}

export default BranchFormInfos;
